import { PlatformLocation } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import {
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import {
  ISecondaryNavigationBarConfig,
  ISecondaryNavigationBarItem,
  MerchandiseService,
} from '@epicuro-next/data/merchandise';
import { BasketItemSource } from '@epicuro-next/models/basket';
import {
  APP_PRIVATE_PATHS,
  APP_TOP_LEVEL_PATH,
} from '@epicuro-next/navigation/constants';
import {
  getListIdFromProductListUrl,
  isListUrl,
  isSearchUrl,
} from '@epicuro-next/navigation/routes';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import {
  EnvironmentSettings,
  ENVIRONMENT_SETTINGS,
} from '@epicuro-next/platform/environment';
import { selectActiveAccountId } from '@epicuro-next/state/accounts-state';
import { setProductSource } from '@epicuro-next/state/config-strip-state';
import { selectUrl } from '@epicuro-next/state/router-store';
import {
  selectSiteId,
  selectSubSiteId,
} from '@epicuro-next/state/session-state';
import { selectIsPowerListPromoted } from '@epicuro-next/state/user-ui-state';
import { isEqual, isNonNullish, toBoolean } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { select, Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

const DEFAULT_URLS_TO_SHOW = [
  APP_PRIVATE_PATHS.home.path,
  APP_PRIVATE_PATHS.search.path,
];

const DEFAULT_MAX_LINKS = 0;
const URL_SEPARATOR = '/';

export type InternalNavigationitem = ISecondaryNavigationBarItem & {
  external?: boolean;
};

export type ISecondaryNavigationNewTabAnchorProps = {
  text: string;
  href: string;
};

export type ISecondaryNavigationPulseAnchorProps = {
  text: string;
  routerLink: string;
  queryParams: { [key: string]: string };
};

export type ISecondaryNavigationAnchorProps =
  | ISecondaryNavigationNewTabAnchorProps
  | ISecondaryNavigationPulseAnchorProps;

export type SecondaryNavigationBarConfiguration = {
  showBurgerMenuOnMobile: boolean;
  items: ISecondaryNavigationBarItem[];
  urls?: string[];
};

@Injectable()
export class SecondaryNavigationBarComponentStore extends ComponentStore<{}> {
  private baseURL = this.location.getBaseHrefFromDOM();

  private maxLinks$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        PulseConfigurationKeys.SecondaryNavigationBarMaxLinks,
        PulseConfigurations
      >(
        PULSE_FEATURE_NAME,
        PulseConfigurationKeys.SecondaryNavigationBarMaxLinks,
        DEFAULT_MAX_LINKS,
      ),
    ),
    map((value) => value as number),
    filter(isNonNullish),
    distinctUntilChanged(),
  );

  private configParams$ = combineLatest([
    this.store.pipe(select(selectActiveAccountId), filter(isNonNullish)),
    this.store.pipe(select(selectSiteId), filter(isNonNullish)),
    this.store.pipe(select(selectSubSiteId), distinctUntilChanged()),
  ]).pipe(
    map(([accountId, siteId, subSiteId]) => ({ accountId, siteId, subSiteId })),
    distinctUntilChanged(isEqual),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public config$ = this.configParams$.pipe(
    distinctUntilChanged(isEqual),
    withLatestFrom(this.maxLinks$),
    filter(([, maxLinks]) => (maxLinks ?? 0) > 0),
    switchMap(([{ accountId, siteId, subSiteId }]) =>
      this.merchandise
        .getSecondaryNavigation(siteId, accountId, subSiteId)
        .pipe(catchError(() => of(undefined))),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public show$ = combineLatest([
    this.config$,
    this.store.pipe(select(selectUrl)),
    this.store.pipe(select(selectIsPowerListPromoted)),
  ]).pipe(
    map(([config, url, isPowerListPromoted]) => {
      if (!config?.items || config.items.length === 0 || isPowerListPromoted) {
        return false;
      }

      const urlsToShow =
        !config.urls || config.urls.length === 0
          ? DEFAULT_URLS_TO_SHOW
          : config.urls;

      const urlPartsWithoutQueryParams = url
        ?.split('?')?.[0]
        ?.split(URL_SEPARATOR);
      return (
        url?.indexOf(
          `${APP_TOP_LEVEL_PATH.cs.outlet}:${APP_TOP_LEVEL_PATH.cs.path}`,
        ) === -1 &&
        urlsToShow
          .map((u) => u.replace(/\//g, ''))
          .some((u) =>
            urlPartsWithoutQueryParams?.some((urlPart) => {
              return urlPart?.includes(u);
            }),
          )
      );
    }),
    startWith(false),
  );

  private origin$ = of(window.location.origin);

  private sitePathWithSlash$ = this.configParams$.pipe(
    map(({ siteId, subSiteId }) => {
      const sitesEnabled = toBoolean(this.environmentSettings.multiSiteSupport);
      if (!sitesEnabled) return '';

      const subSiteStr = subSiteId ? `:${subSiteId}` : '';
      return `${APP_TOP_LEVEL_PATH.siteSelector.custom}/${siteId}${subSiteStr}`;
    }),
  );

  public items$ = this.config$.pipe(
    filter(isNonNullish),
    withLatestFrom(this.origin$, this.sitePathWithSlash$),
    map(([config, origin, sitePathWithSlash]) => ({
      config: transformConfigurationLinks(
        config,
        origin,
        this.baseURL,
        sitePathWithSlash,
      ),
      sitePath: sitePathWithSlash,
    })),
    withLatestFrom(this.maxLinks$),
    map(([{ config, sitePath }, maxLinks]) => {
      const slicedItems = (config?.items ?? []).slice(0, maxLinks);
      return slicedItems
        .map((item): InternalNavigationitem => {
          let { link } = item;
          if (link.startsWith('http')) return { ...item, external: true };

          link = link.replace('/next', '/');

          if (sitePath && link.includes(sitePath)) {
            return {
              ...item,
              link: removeMultipleSlashes(`${this.baseURL}${link}`),
            };
          }

          return {
            ...item,
            link: removeMultipleSlashes(`${this.baseURL}${sitePath}/${link}`),
          };
        })
        .map((item) => linkItemToAnchorProps(item));
    }),
  );

  constructor(
    private store: Store,
    private merchandise: MerchandiseService,
    private location: PlatformLocation,
    @Inject(ENVIRONMENT_SETTINGS)
    private environmentSettings: EnvironmentSettings,
  ) {
    super();
  }

  public setProductSource(anchor: ISecondaryNavigationPulseAnchorProps) {
    if (isSearchUrl(anchor.routerLink)) {
      const details = {
        query: anchor?.queryParams?.q ?? '',
        searchParams: anchor?.queryParams ?? {},
      };

      this.store.dispatch(
        setProductSource({
          source: BasketItemSource.SEARCH_SECONDARY_NAVIGATION,
          details: {
            ...details,
            linkText: anchor.text,
            linkUrl: anchorPropsToHref(anchor),
          },
          additionalUiDetails: details,
        }),
      );
      return;
    }

    // no real usage example found, but theoretically possible
    if (isListUrl(anchor.routerLink)) {
      this.store.dispatch(
        setProductSource({
          source: BasketItemSource.LIST_SECONDARY_NAVIGATION,
          details: {
            listId: [getListIdFromProductListUrl(anchor.routerLink)],
          },
        }),
      );
      return;
    }
  }
}

function linkItemToAnchorProps(
  item: InternalNavigationitem,
): ISecondaryNavigationAnchorProps {
  const base = { text: item.text };
  if (item.external) return { ...base, href: item.link };

  const parts = item.link.split('?');
  const urlSearchParams = new URLSearchParams(parts[1]);
  const queryParams: { [key: string]: string } = {};
  urlSearchParams.forEach((value, key) => (queryParams[key] = value));

  return {
    ...base,
    queryParams,
    routerLink: parts[0],
  };
}

function removeMultipleSlashes(str: string) {
  // once safari will support negative lookbehind update use this regex:
  // /(?<!:)\/+/g
  let result = str.replace(/([^:]\/)\/+/g, '$1');

  // remove once regex is updated
  if (result.startsWith('//')) {
    result = result.substring(1);
  }

  return result;
}

function transformConfigurationLinks(
  config: SecondaryNavigationBarConfiguration,
  origin: string,
  baseUrl: string,
  sitePathWithSlash: string,
): ISecondaryNavigationBarConfig {
  config.items = config.items.map((item) => {
    if (!item.link.includes(origin)) {
      return item;
    }

    let newLink = item.link.replace(
      removeMultipleSlashes(`${origin}${baseUrl}${sitePathWithSlash}`),
      '',
    );

    if (!newLink.startsWith('/') && !newLink.startsWith('http')) {
      newLink = `/${newLink}`;
    }
    return {
      ...item,
      link: newLink,
    };
  });
  return config;
}

function anchorPropsToHref(routeProps: ISecondaryNavigationPulseAnchorProps) {
  const url = new URL(routeProps.routerLink ?? '', window.location.origin);
  Object.keys(routeProps.queryParams ?? {}).forEach((key) => {
    url.searchParams.append(key, routeProps.queryParams?.[key] ?? '');
  });

  return url.href;
}
